import { useState, useMemo, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Sheet, SheetContent } from "./ui/sheet";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import {
  LayoutDashboard,
  Users,
  Church,
  Briefcase,
  Music,
  Calendar,
  ClipboardList,
  DollarSign,
  UserCog,
  Settings,
  Newspaper,
  Menu,
  LogOut,
  Building2,
  Heart,
  BookOpen,
  MessageCircle,
  Footprints,
  HeartHandshake,
  X,
  Plus,
  Wallet,
  FileText,
  BarChart3,
  Gift,
  Bell,
  Wifi,
  UserCheck,
  ChevronRight,
  ChevronDown,
  Home,
  Grid3X3,
  Search,
  ExternalLink,
  Globe,
  UserPlus
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";

// Available shortcuts for configuration
export const availableShortcuts = [
  { value: "inicio", label: "Início", path: "/dashboard", icon: Home },
  { value: "news", label: "News", path: "/news", icon: Newspaper },
  { value: "ministerios", label: "Ministérios", path: "/ministerios", icon: Church },
  { value: "pessoas", label: "Pessoas", path: "/pessoas", icon: Users },
  { value: "projetos", label: "Projetos", path: "/projetos", icon: Briefcase },
  { value: "musicas", label: "Músicas", path: "/musicas", icon: Music },
  { value: "eventos", label: "Eventos", path: "/eventos", icon: Calendar },
  { value: "frequencia", label: "Frequência", path: "/frequencia", icon: UserCheck },
  { value: "trilho", label: "Trilho", path: "/trilho", icon: Footprints },
  { value: "conexoes", label: "Conexões", path: "/conexoes", icon: HeartHandshake },
  { value: "whatsapp", label: "WhatsApp", path: "/whatsapp", icon: MessageCircle },
  { value: "devocional", label: "Devocional", path: "/devocional", icon: BookOpen },
  { value: "escalas", label: "Escalas", path: "/escalas", icon: ClipboardList },
  { value: "financeiro", label: "Financeiro", path: "/financeiro", icon: DollarSign },
  { value: "inscricoes", label: "Inscrições", path: "/social", icon: UserPlus },
  { value: "configuracoes", label: "Configurações", path: "/configuracoes", icon: Settings },
];

// Default bottom navigation config
export const defaultBottomNavConfig = [
  { value: "inicio", label: "Início", path: "/dashboard", icon: "Home" },
  { value: "news", label: "News", path: "/news", icon: "Newspaper" },
  { value: "menu", label: "Menu", path: "menu", icon: "Grid3X3", isMenu: true },
  { value: "devocional", label: "Devocional", path: "/devocional", icon: "BookOpen" },
  { value: "eventos", label: "Eventos", path: "/eventos", icon: "Calendar" },
];

// Icon mapping for dynamic configuration
const iconMap = {
  Home, Newspaper, Church, Users, Briefcase, Music, Calendar, Footprints, HeartHandshake, 
  MessageCircle, BookOpen, ClipboardList, DollarSign, Settings, Grid3X3,
  Heart, Bell, Wifi, Gift, BarChart3, FileText, Wallet, UserCog, UserPlus, Globe: ExternalLink
};

// Helper to get icon component from string
const getIconComponent = (iconName) => {
  return iconMap[iconName] || Home;
};

// Menu categories for the central menu modal
const menuCategories = [
  {
    title: "PRINCIPAL",
    color: "text-blue-600",
    items: [
      { path: "/dashboard", label: "Início", icon: Home },
      { path: "/news", label: "News", icon: Newspaper },
      { path: "/escalas", label: "Escalas", icon: ClipboardList },
      { path: "/eventos", label: "Eventos", icon: Calendar },
      { path: "/frequencia", label: "Frequência", icon: UserCheck },
    ]
  },
  {
    title: "GESTÃO",
    color: "text-green-600",
    items: [
      { path: "/pessoas", label: "Pessoas", icon: Users },
      { path: "/ministerios", label: "Ministérios", icon: Church },
      { path: "/musicas", label: "Músicas", icon: Music },
      { path: "/trilho", label: "Trilho do Reino", icon: Footprints },
      { path: "/conexoes", label: "Igreja Conectada", icon: HeartHandshake },
      { path: "/social", label: "Inscrições", icon: UserPlus },
    ]
  },
  {
    title: "FINANCEIRO",
    color: "text-amber-600",
    items: [
      { path: "/financeiro", label: "Financeiro", icon: DollarSign },
      { path: "/financeiro/inscricoes", label: "Inscrições", icon: FileText },
    ]
  },
  {
    title: "COMUNICAÇÃO",
    color: "text-purple-600",
    items: [
      { path: "/whatsapp", label: "WhatsApp", icon: MessageCircle },
      { path: "/devocional", label: "Devocional", icon: BookOpen },
    ]
  },
  {
    title: "CONFIGURAÇÕES",
    color: "text-gray-600",
    items: [
      { path: "/configuracoes", label: "Configurações", icon: Settings },
      { path: "/usuarios", label: "Usuários", icon: UserCog },
      { path: "/news-config", label: "Config. News", icon: Newspaper },
      { path: "/devocional-config", label: "Config. Devocional", icon: BookOpen },
      { path: "/formulario-config", label: "Config. Formulários", icon: FileText },
    ]
  },
];

const MobileLayoutInner = ({ children }) => {
  const { user, logout, currentBranch, branches, branchSettings } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // Bottom navigation items - read from branch config or use defaults
  const bottomNavItems = useMemo(() => {
    // Check if branch has custom bottom nav config
    const branchConfig = currentBranch?.bottom_nav_config;
    
    if (branchConfig && Array.isArray(branchConfig) && branchConfig.length > 0) {
      // Use branch custom configuration
      return branchConfig.map(item => ({
        ...item,
        icon: getIconComponent(item.icon),
        isExternal: item.path?.startsWith('http')
      }));
    }
    
    // Default configuration: Início | News | Menu | Devocional | Eventos
    return defaultBottomNavConfig.map(item => ({
      ...item,
      icon: getIconComponent(item.icon)
    }));
  }, [currentBranch?.bottom_nav_config]);

  // Helper functions
  const getBranchName = (branch) => {
    if (!branch) return "Selecionar";
    if (typeof branch === 'object' && branch.nome) return branch.nome;
    if (typeof branch === 'string') {
      const found = branches.find(b => b.id === branch);
      return found?.nome || "Carregando...";
    }
    if (typeof branch === 'object' && branch.id) {
      const found = branches.find(b => b.id === branch.id);
      return found?.nome || "Carregando...";
    }
    return "Selecionar";
  };

  const getBranchType = (branch) => {
    if (!branch) return null;
    if (typeof branch === 'object' && branch.tipo) return branch.tipo;
    if (typeof branch === 'string' || (typeof branch === 'object' && branch.id)) {
      const id = typeof branch === 'string' ? branch : branch.id;
      const found = branches.find(b => b.id === id);
      return found?.tipo || null;
    }
    return null;
  };

  const getLogoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate("/login");
  };

  const handleNavClick = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const hasPermission = (module) => {
    if (!user?.perfil_acesso_id) return true;
    const permissoes = user?.permissoes || {};
    return permissoes[module]?.ver === true;
  };

  const isAdmin = () => {
    if (!user?.perfil_acesso_id) return true;
    const permissoes = user?.permissoes || {};
    return permissoes?.configuracoes?.ver === true;
  };

  const primaryColor = branchSettings?.cor_primaria || "#2563eb";

  return (
    <div className="min-h-screen bg-background" style={{ paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}>
      {/* Header - Simplificado */}
      <header className="sticky top-0 z-40 bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {branchSettings?.logo_url ? (
            <img 
              src={getLogoUrl(branchSettings.logo_url)} 
              alt="Logo" 
              className="h-8 object-contain"
            />
          ) : (
            <span className="text-lg font-bold" style={{ color: primaryColor }}>8 Life</span>
          )}
          
          {currentBranch && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground border-l pl-3">
              {getBranchType(currentBranch) === "instituto_social" ? (
                <Heart className="h-3 w-3 text-pink-500" />
              ) : (
                <Building2 className="h-3 w-3" />
              )}
              <span className="truncate max-w-[100px]">{getBranchName(currentBranch)}</span>
            </div>
          )}
        </div>

        <button
          onClick={() => navigate("/meu-perfil")}
          className="w-9 h-9 rounded-full flex items-center justify-center transition-all"
          style={{ backgroundColor: `${primaryColor}15` }}
        >
          <span style={{ color: primaryColor }} className="text-sm font-semibold">
            {user?.nome?.charAt(0)?.toUpperCase()}
          </span>
        </button>
      </header>

      {/* Main Content */}
      <main className="container-mobile">
        {children}
      </main>

      {/* Bottom Navigation - Modern 5-item with center menu */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t z-50" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="flex items-center justify-around max-w-lg mx-auto h-[72px] px-1">
          {bottomNavItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = !item.isMenu && location.pathname === item.path;
            
            if (item.isMenu) {
              return (
                <button
                  key="menu"
                  onClick={() => setMenuOpen(true)}
                  className="flex flex-col items-center justify-center gap-1 -mt-5 transition-transform active:scale-95"
                  data-testid="bottom-nav-menu"
                >
                  <div 
                    className="w-14 h-14 rounded-2xl shadow-lg flex items-center justify-center"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Grid3X3 className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-[10px] font-medium text-gray-500">Menu</span>
                </button>
              );
            }
            
            // Handle external links
            if (item.isExternal) {
              return (
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center gap-1 py-2 px-2 min-w-[60px] rounded-xl transition-all text-gray-400 hover:text-gray-600"
                  data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gray-50">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="text-[10px] font-medium">{item.label}</span>
                </a>
              );
            }
            
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center justify-center gap-1 py-2 px-2 min-w-[60px] rounded-xl transition-all ${
                  isActive 
                    ? "" 
                    : "text-gray-400 hover:text-gray-600"
                }`}
                data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              >
                <div 
                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                    isActive ? "" : "bg-gray-50"
                  }`}
                  style={isActive ? { backgroundColor: `${primaryColor}15` } : {}}
                >
                  <Icon 
                    className="h-5 w-5" 
                    style={isActive ? { color: primaryColor } : {}}
                  />
                </div>
                <span 
                  className="text-[10px] font-semibold"
                  style={isActive ? { color: primaryColor } : {}}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Menu Modal - Full screen sheet from bottom */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl p-0">
          <div className="flex flex-col h-full">
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
            </div>
            
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h2 className="text-lg font-bold">Menu</h2>
              <Button variant="ghost" size="icon" onClick={() => setMenuOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* User Info */}
            <div className="px-5 py-4 border-b bg-gray-50">
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}20` }}
                >
                  <span style={{ color: primaryColor }} className="text-lg font-semibold">
                    {user?.nome?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{user?.nome}</p>
                  <p className="text-sm text-muted-foreground">{user?.perfil_nome || user?.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleNavClick("/meu-perfil")}>
                    <UserCog className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-600 border-red-200 hover:bg-red-50">
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Menu Categories */}
            <ScrollArea className="flex-1 px-5 py-4">
              <div className="space-y-6">
                {menuCategories.map((category) => {
                  // Filter items based on permissions
                  const visibleItems = category.items.filter(item => {
                    if (item.path === "/configuracoes" || item.path === "/usuarios" || item.path === "/devocional-config") {
                      return isAdmin();
                    }
                    if (item.path === "/financeiro" || item.path === "/financeiro/inscricoes") {
                      return hasPermission("financeiro");
                    }
                    return true;
                  });

                  if (visibleItems.length === 0) return null;

                  return (
                    <div key={category.title}>
                      <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${category.color}`}>
                        {category.title}
                      </h3>
                      <div className="grid grid-cols-4 gap-3">
                        {visibleItems.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;
                          
                          return (
                            <button
                              key={item.path}
                              onClick={() => handleNavClick(item.path)}
                              className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all ${
                                isActive 
                                  ? "bg-primary/10 text-primary" 
                                  : "hover:bg-gray-100"
                              }`}
                            >
                              <div 
                                className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                  isActive ? "" : "bg-gray-100"
                                }`}
                                style={isActive ? { backgroundColor: `${primaryColor}20` } : {}}
                              >
                                <Icon 
                                  className="h-6 w-6" 
                                  style={isActive ? { color: primaryColor } : {}}
                                />
                              </div>
                              <span className="text-xs font-medium text-center leading-tight">
                                {item.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            {/* Footer with logout */}
            <div className="p-4 border-t mt-auto">
              <Button 
                variant="outline" 
                className="w-full justify-center gap-2 h-11" 
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
                Sair da conta
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

// Desktop menu items for top navigation
const desktopMenuItems = [
  {
    label: "Início",
    path: "/dashboard",
    icon: Home,
  },
  {
    label: "News",
    path: "/news",
    icon: Newspaper,
  },
  {
    label: "Gestão",
    icon: Users,
    children: [
      { path: "/pessoas", label: "Pessoas", icon: Users },
      { path: "/ministerios", label: "Ministérios", icon: Church },
      { path: "/musicas", label: "Músicas", icon: Music },
      { path: "/trilho", label: "Trilho do Reino", icon: Footprints },
      { path: "/conexoes", label: "Igreja Conectada", icon: HeartHandshake },
    ]
  },
  {
    label: "Atividades",
    icon: Calendar,
    children: [
      { path: "/escalas", label: "Escalas", icon: ClipboardList },
      { path: "/eventos", label: "Eventos", icon: Calendar },
      { path: "/frequencia", label: "Frequência", icon: UserCheck },
      { path: "/projetos", label: "Projetos", icon: Briefcase },
      { path: "/social", label: "Inscrições", icon: UserPlus },
    ]
  },
  {
    label: "Financeiro",
    icon: DollarSign,
    children: [
      { path: "/financeiro", label: "Visão Geral", icon: DollarSign },
      { path: "/financeiro/inscricoes", label: "Inscrições", icon: FileText },
    ],
    permission: "financeiro"
  },
  {
    label: "Comunicação",
    icon: MessageCircle,
    children: [
      { path: "/whatsapp", label: "WhatsApp", icon: MessageCircle },
      { path: "/devocional", label: "Devocional", icon: BookOpen },
    ]
  },
  {
    label: "Config",
    icon: Settings,
    children: [
      { path: "/configuracoes", label: "Configurações", icon: Settings },
      { path: "/usuarios", label: "Usuários", icon: UserCog },
      { path: "/news-config", label: "Config. News", icon: Newspaper },
      { path: "/devocional-config", label: "Config. Devocional", icon: BookOpen },
      { path: "/formulario-config", label: "Config. Formulários", icon: FileText },
    ]
  },
];

// Desktop Layout Component
const DesktopLayout = ({ children }) => {
  const { user, logout, currentBranch, branches, branchSettings } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Helper functions
  const getBranchName = (branch) => {
    if (!branch) return "Selecionar";
    if (typeof branch === 'object' && branch.nome) return branch.nome;
    if (typeof branch === 'string') {
      const found = branches.find(b => b.id === branch);
      return found?.nome || "Carregando...";
    }
    return "Selecionar";
  };

  const getBranchType = (branch) => {
    if (!branch) return null;
    if (typeof branch === 'object' && branch.tipo) return branch.tipo;
    if (typeof branch === 'string') {
      const found = branches.find(b => b.id === branch);
      return found?.tipo || null;
    }
    return null;
  };

  const getLogoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const hasPermission = (module) => {
    if (!user?.perfil_acesso_id) return true;
    const permissoes = user?.permissoes || {};
    return permissoes[module]?.ver === true;
  };

  const isAdmin = () => {
    if (!user?.perfil_acesso_id) return true;
    const permissoes = user?.permissoes || {};
    return permissoes?.configuracoes?.ver === true;
  };

  const primaryColor = branchSettings?.cor_primaria || "#2563eb";

  const isPathActive = (path) => location.pathname === path;
  const isChildActive = (children) => children?.some(c => location.pathname === c.path);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar - Safari Style */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Logo & Branch */}
            <div className="flex items-center gap-4">
              {branchSettings?.logo_url ? (
                <img 
                  src={getLogoUrl(branchSettings.logo_url)} 
                  alt="Logo" 
                  className="h-8 object-contain"
                />
              ) : (
                <span className="text-xl font-bold" style={{ color: primaryColor }}>8 Life</span>
              )}
              
              {currentBranch && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground border-l pl-4">
                  {getBranchType(currentBranch) === "instituto_social" ? (
                    <Heart className="h-4 w-4 text-pink-500" />
                  ) : (
                    <Building2 className="h-4 w-4" />
                  )}
                  <span>{getBranchName(currentBranch)}</span>
                </div>
              )}
            </div>

            {/* Navigation Menu */}
            <nav className="flex items-center gap-1">
              {desktopMenuItems.map((item) => {
                // Check permission
                if (item.permission && !hasPermission(item.permission)) return null;

                // Simple link (no children)
                if (!item.children) {
                  const Icon = item.icon;
                  const isActive = isPathActive(item.path);
                  
                  return (
                    <Button
                      key={item.path}
                      variant={isActive ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => navigate(item.path)}
                      className={`gap-2 ${isActive ? 'bg-gray-100' : ''}`}
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Button>
                  );
                }

                // Dropdown menu
                const Icon = item.icon;
                const isActive = isChildActive(item.children);
                
                return (
                  <DropdownMenu key={item.label}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant={isActive ? "secondary" : "ghost"}
                        size="sm"
                        className={`gap-1 ${isActive ? 'bg-gray-100' : ''}`}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                        <ChevronDown className="h-3 w-3 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const childActive = isPathActive(child.path);
                        
                        return (
                          <DropdownMenuItem
                            key={child.path}
                            onClick={() => navigate(child.path)}
                            className={`gap-2 cursor-pointer ${childActive ? 'bg-gray-100' : ''}`}
                          >
                            <ChildIcon className="h-4 w-4" />
                            {child.label}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })}

            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 hover:bg-gray-100 rounded-lg px-2 py-1.5 transition-colors">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${primaryColor}15` }}
                    >
                      <span style={{ color: primaryColor }} className="text-sm font-semibold">
                        {user?.nome?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-left hidden sm:block">
                      <p className="text-sm font-medium leading-none">{user?.nome?.split(" ")[0]}</p>
                      <p className="text-xs text-muted-foreground">{user?.perfil_nome || "Usuário"}</p>
                    </div>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-medium">{user?.nome}</span>
                      <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/meu-perfil")} className="gap-2 cursor-pointer">
                    <UserCog className="h-4 w-4" />
                    Meu Perfil
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="gap-2 cursor-pointer text-red-600">
                    <LogOut className="h-4 w-4" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
};

// Responsive Layout Wrapper - Chooses Mobile or Desktop based on screen size
export const MobileLayout = ({ children }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isMobile) {
    return <MobileLayoutInner>{children}</MobileLayoutInner>;
  }

  return <DesktopLayout>{children}</DesktopLayout>;
};

export default MobileLayout;
